// frontend/src/pages/Socrates/utils/parameterHelpers.js

/**
 * Utility functions for parameter formatting and manipulation
 */

/**
 * Format parameter name for display
 * @param {string} parameterName - Raw parameter name
 * @returns {string} Formatted parameter name
 */
export const formatParameterName = (parameterName) => {
  if (!parameterName) return '';
  
  // Remove common prefixes
  let formatted = parameterName.replace(/^plato_/, '');
  
  // Convert snake_case to Title Case
  formatted = formatted
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  return formatted;
};

/**
 * Format parameter type for display
 * @param {string} parameterType - Raw parameter type
 * @returns {string} Formatted parameter type
 */
export const formatParameterType = (parameterType) => {
  if (!parameterType) return '';
  
  const typeMap = {
    'string': 'Text',
    'number': 'Number',
    'integer': 'Integer',
    'boolean': 'Boolean',
    'object': 'Selection',
    'array': 'List'
  };
  
  return typeMap[parameterType.toLowerCase()] || parameterType;
};

/**
 * Get parameter category display name
 * @param {string} categoryName - Raw category name
 * @returns {string} Formatted category name
 */
export const formatCategoryName = (categoryName) => {
  if (!categoryName) return '';
  
  // Remove plato_ prefix and format
  return categoryName
    .replace(/^plato_/, '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Validate parameter value based on type and constraints
 * @param {any} value - Value to validate
 * @param {Object} parameter - Parameter definition
 * @returns {Object} Validation result
 */
export const validateParameterValue = (value, parameter) => {
  const result = { isValid: true, errors: [] };
  
  if (!parameter) {
    result.isValid = false;
    result.errors.push('Parameter definition is required');
    return result;
  }
  
  // Check required
  if (parameter.is_required && (value === null || value === undefined || value === '')) {
    result.isValid = false;
    result.errors.push('This parameter is required');
    return result;
  }
  
  // Skip validation if value is empty and not required
  if (!parameter.is_required && (value === null || value === undefined || value === '')) {
    return result;
  }
  
  // Type-specific validation
  switch (parameter.parameter_type) {
    case 'number':
    case 'integer':
      const numValue = Number(value);
      if (isNaN(numValue)) {
        result.isValid = false;
        result.errors.push('Must be a valid number');
      } else {
        if (parameter.min_value !== undefined && numValue < parameter.min_value) {
          result.isValid = false;
          result.errors.push(`Must be at least ${parameter.min_value}`);
        }
        if (parameter.max_value !== undefined && numValue > parameter.max_value) {
          result.isValid = false;
          result.errors.push(`Must be at most ${parameter.max_value}`);
        }
        if (parameter.parameter_type === 'integer' && !Number.isInteger(numValue)) {
          result.isValid = false;
          result.errors.push('Must be a whole number');
        }
      }
      break;
      
    case 'string':
      if (typeof value !== 'string') {
        result.isValid = false;
        result.errors.push('Must be text');
      } else {
        if (parameter.min_length && value.length < parameter.min_length) {
          result.isValid = false;
          result.errors.push(`Must be at least ${parameter.min_length} characters`);
        }
        if (parameter.max_length && value.length > parameter.max_length) {
          result.isValid = false;
          result.errors.push(`Must be at most ${parameter.max_length} characters`);
        }
      }
      break;
      
    case 'boolean':
      if (typeof value !== 'boolean') {
        result.isValid = false;
        result.errors.push('Must be true or false');
      }
      break;
  }
  
  return result;
};

/**
 * Get default value for a parameter
 * @param {Object} parameter - Parameter definition
 * @returns {any} Default value
 */
export const getParameterDefaultValue = (parameter) => {
  if (!parameter) return null;
  
  if (parameter.default_value !== undefined) {
    return parameter.default_value;
  }
  
  // Type-based defaults
  switch (parameter.parameter_type) {
    case 'string':
      return '';
    case 'number':
    case 'integer':
      return parameter.min_value || 0;
    case 'boolean':
      return false;
    case 'object':
      return null;
    case 'array':
      return [];
    default:
      return null;
  }
};